import React, { useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, CircleIcon, UploadIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Field, Input, Select } from '../../components/ui/Field';
import { Toggle } from '../../components/ui/ChoiceGroup';
import { Logo } from '../../components/shell/Logo';
import { useAuth } from '../../contexts/AuthContext';
import { CONSOLES } from '../../contexts/AccountContext';
import { institutions } from '../../data/institutions';
import { onboardingProgress } from '../../utils/auth';
const INTERESTS = ['Accommodation', 'Marketplace', 'Library', 'Gigs', 'Events', 'Mentorship', 'Academic services', 'Roommates'];

/** The seven steps progress is measured against, in the order they are asked. */
const STEPS = [{
  key: 'username',
  label: 'Your handle'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'campus',
  label: 'Campus'
}, {
  key: 'programme',
  label: 'Programme'
}, {
  key: 'photo',
  label: 'Photo'
}, {
  key: 'interests',
  label: 'Interests'
}, {
  key: 'notifications',
  label: 'Notifications'
}] as const;
type StepKey = (typeof STEPS)[number]['key'];
const LEVELS = ['100 level', '200 level', '300 level', '400 level', '500 level', 'Postgraduate', 'Graduated'];

/**
 * Progressive onboarding, one decision at a time.
 *
 * Seven steps are counted, so seven steps are asked — a progress figure that
 * measures something the person was never shown is just a number going up.
 * Every step is skippable, nothing here blocks using Campuzon, and the finish
 * lands on the console the account actually holds rather than always the
 * student home.
 */
export function Onboarding() {
  const {
    identity,
    updateProfile,
    updateOnboarding
  } = useAuth();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [institutionId, setInstitutionId] = useState(identity?.institutionId ?? '');
  const [campusId, setCampusId] = useState(identity?.campusId ?? '');
  const [programme, setProgramme] = useState(identity?.programme ?? '');
  const [level, setLevel] = useState(identity?.level ?? '');
  const [interests, setInterests] = useState<string[]>(identity?.interests ?? []);
  const [notifications, setNotifications] = useState(true);
  const [photo, setPhoto] = useState<string | null>(identity?.photoUrl ?? null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Where a finished account should land. Someone who only holds a property
  // role has no student home to be dropped on.
  const destination = useMemo(() => {
    if (!identity) return '/';
    if (identity.roles.includes('student')) return '/';
    const console = CONSOLES.find(entry => identity.roles.includes(entry.role));
    return console?.path ?? '/';
  }, [identity]);
  if (!identity) return <Navigate to="/signin" replace data-id="element-7278" data-mp="3dj7vjdesf2cx" />;
  const progress = onboardingProgress(identity);
  const institution = institutions.find(item => item.id === institutionId);
  const step = STEPS[index];
  const last = index === STEPS.length - 1;
  const doneByKey: Record<StepKey, boolean> = {
    username: identity.onboarding.usernameConfirmed,
    email: identity.emailState === 'verified',
    campus: identity.onboarding.campusSelected,
    programme: identity.onboarding.programmeAdded,
    photo: identity.onboarding.photoAdded,
    interests: identity.onboarding.interestsAdded,
    notifications: identity.onboarding.notificationsSet
  };

  /** Saves the current step, then advances. Skipping saves nothing. */
  function commit() {
    if (step.key === 'username') updateOnboarding({
      usernameConfirmed: true
    });
    if (step.key === 'campus' && institutionId) {
      updateProfile({
        institutionId,
        campusId: campusId || null
      });
      updateOnboarding({
        campusSelected: true
      });
    }
    if (step.key === 'programme') {
      updateProfile({
        programme: programme || null,
        level: level || null
      });
      updateOnboarding({
        programmeAdded: Boolean(programme.trim())
      });
    }
    if (step.key === 'photo' && photo) {
      updateProfile({
        photoUrl: photo
      });
      updateOnboarding({
        photoAdded: true
      });
    }
    if (step.key === 'interests') {
      updateProfile({
        interests
      });
      updateOnboarding({
        interestsAdded: interests.length > 0
      });
    }
    if (step.key === 'notifications') updateOnboarding({
      notificationsSet: true
    });
    if (last) navigate(destination, {
      replace: true
    });else setIndex(current => current + 1);
  }
  function leave() {
    updateOnboarding({
      dismissed: true
    });
    navigate(destination, {
      replace: true
    });
  }
  function pickPhoto(file: File | undefined) {
    setPhotoError(null);
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoError('That file type is not accepted. Use a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('That image is larger than 5 MB. Choose a smaller one.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(String(reader.result));
    reader.readAsDataURL(file);
  }
  return <div className="min-h-screen bg-canvas" data-id="element-7279" data-mp="2lqyrcfg0rpjg">
      <header className="border-b border-line bg-surface" data-id="element-7280" data-mp="2ls1kku0te42j">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6" data-id="element-7281" data-mp="39gkt9ans0iv5">
          <Logo subtitle="Setting up" data-id="element-7282" data-mp="3dftko8vcg4nj" />
          <button type="button" onClick={leave} className="cz-inline-link min-h-touch text-body-sm" data-id="element-7283" data-mp="3dixtxkno1ql2">
            Finish later
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12" data-id="element-7284" data-mp="34mymu087hb7s">
        <h1 className="text-h2 font-semibold text-ink" data-id="element-7285" data-mp="3sdcgtwacpk50">Welcome, {identity.displayName}</h1>
        <p className="mt-2.5 max-w-[62ch] text-body-lg text-ink-muted" data-id="element-7286" data-mp="zy943ps8y06">
          Seven short steps. Skip any of them — Campuzon works without them, it is just less useful.
        </p>

        {/* Progress counts exactly the steps below, so the figure means something. */}
        <div className="mt-6 rounded-card border border-line bg-surface p-5" data-id="element-7287" data-mp="xiryssw1f0k">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-7288" data-mp="zllvnx5vsak">
            <p className="text-body font-semibold text-ink" data-id="element-7289" data-mp="3s3agjnr0ojl9">
              Step {index + 1} of {STEPS.length} · {step.label}
            </p>
            <Badge tone={progress.percent === 100 ? 'ok' : 'neutral'} plain data-id="element-7290" data-mp="3jbry166llrp0">
              {progress.done} of {progress.total} complete
            </Badge>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-canvas" role="img" aria-label={`Profile ${progress.percent} percent complete`} data-id="element-7291" data-mp="3eaaz3g7ocfb2">
            <div className="h-full rounded-full bg-brand-700 transition-[width] duration-200 ease-out" style={{
            width: `${progress.percent}%`
          }} data-id="element-7292" data-mp="34dg4v9ifp12l" />
          </div>

          <ol className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-line pt-4" data-id="element-7293" data-mp="1fwobgoi81kf9">
            {STEPS.map((entry, position) => <li key={entry.key} data-id="element-7294" data-mp="1ourwzkt7ih72" data-mpi={position}>
                <button type="button" onClick={() => setIndex(position)} aria-current={position === index ? 'step' : undefined} className={`inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium ${position === index ? 'text-ink' : doneByKey[entry.key] ? 'text-ok-700' : 'text-ink-subtle hover:text-ink'}`} data-id="element-7295" data-mp="1owl1atgckpbw">
                  {doneByKey[entry.key] ? <CheckIcon aria-hidden className="h-4 w-4" data-id="element-7296" data-mp="dfbmik85ylgx" /> : <CircleIcon aria-hidden className="h-4 w-4" data-id="element-7297" data-mp="ie62zfinrxxv" />}
                  {entry.label}
                </button>
              </li>)}
          </ol>
        </div>

        <section className="mt-6 rounded-card border border-line bg-surface p-5 sm:p-6" data-id="element-7298" data-mp="32z85n0szvxoa">
          {step.key === 'username' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7299" data-mp="3gvlb1bk988me">This is how people will find you</h2>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7300" data-mp="3c17mnt40f1r8">
                Your handle is a public label, not your account. Campuzon identifies you by an
                internal id that never changes, so you can change this later without breaking
                anything that points at you.
              </p>
              <p className="cz-num mt-4 text-h3 font-semibold text-ink" data-id="element-7301" data-mp="3vmc22he0v3og">@{identity.username}</p>
              <p className="mt-3 text-body-sm text-ink-muted" data-id="element-7302" data-mp="dgzc1nhvbap7">
                Keep it, or change it later in Settings — once every 30 days.
              </p>
            </>}

          {step.key === 'email' && <>
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7303" data-mp="s592z6350qp8">
                <h2 className="text-h4 font-semibold text-ink" data-id="element-7304" data-mp="x1zklxulbucr">Verify your email</h2>
                <Badge tone={identity.emailState === 'verified' ? 'ok' : 'warn'} data-id="element-7305" data-mp="i69y7ylhtf7i">
                  {identity.emailState === 'verified' ? 'Verified' : 'Not verified'}
                </Badge>
              </div>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7306" data-mp="9d6oa0a23khm">
                {identity.emailState === 'verified' ? 'Done. Your account is fully active.' : 'You can explore Campuzon unverified, but applying, ordering and paying need a verified email.'}
              </p>
              {identity.emailState !== 'verified' && <Button variant="secondary" className="mt-4" onClick={() => navigate('/verify-email')} data-id="element-7307" data-mp="3mk894wqv1r4z">
                  Verify now
                </Button>}
            </>}

          {step.key === 'campus' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7308" data-mp="3n4zzoc2lh54j">Your campus</h2>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7309" data-mp="394p8f7xdhoiu">
                This decides which housing, events and providers you see. Not at a university? Leave
                it blank — Campuzon works for graduates, providers and landlords too.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-7310" data-mp="9p2ct012kfaz">
                <Field label="Institution" htmlFor="institution" data-id="element-7311" data-mp="9oairtn4epmt">
                  <Select id="institution" value={institutionId} onChange={event => {
                setInstitutionId(event.target.value);
                setCampusId('');
              }} data-id="element-7312" data-mp="3s3urg3iji40s">
                    <option value="" data-id="element-7313" data-mp="sg389e2zdsps">Not at a university</option>
                    {institutions.map(item => <option key={item.id} value={item.id} data-id="element-7314" data-mp="2d4xd1dj37zmq">
                        {item.name}
                      </option>)}
                  </Select>
                </Field>
                <Field label="Campus" htmlFor="campus" data-id="element-7315" data-mp="2muqmo8zo64oc">
                  <Select id="campus" value={campusId} onChange={event => setCampusId(event.target.value)} disabled={!institution} data-id="element-7316" data-mp="2hwp2tluf61bt">
                    <option value="" data-id="element-7317" data-mp="1p931kk8nz8s4">Choose a campus</option>
                    {institution?.campuses.map(campus => <option key={campus.id} value={campus.id} data-id="element-7318" data-mp="246vdl2f05f1k">
                        {campus.name}
                      </option>)}
                  </Select>
                </Field>
              </div>
            </>}

          {step.key === 'programme' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7319" data-mp="3al35iewgdltp">Your programme</h2>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7320" data-mp="3hnrh5d3bq5fw">
                What you enter here is your own statement. It is not verified, and it never appears
                as though your institution confirmed it — that is a separate step in Academic
                identity.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-7321" data-mp="fo068nwg2us">
                <Field label="Course or programme of study" htmlFor="programme" data-id="element-7322" data-mp="2yyunnbfr3gyq">
                  <Input id="programme" value={programme} onChange={event => setProgramme(event.target.value)} placeholder="BSc Computer Science" data-id="element-7323" data-mp="33wmoww2acffl" />
                </Field>
                <Field label="Level" htmlFor="level" data-id="element-7324" data-mp="3dosg43kpqstq">
                  <Select id="level" value={level} onChange={event => setLevel(event.target.value)} data-id="element-7325" data-mp="2g6iikgxpq1kw">
                    <option value="" data-id="element-7326" data-mp="12btqad0id1rc">Not applicable</option>
                    {LEVELS.map(item => <option key={item} value={item} data-id="element-7327" data-mp="y9ow9d65e346">
                        {item}
                      </option>)}
                  </Select>
                </Field>
              </div>
            </>}

          {step.key === 'photo' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7328" data-mp="jd7loo5s8sio">Add a photo</h2>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7329" data-mp="elzgk4bhjtbb">
                Optional. It helps a roommate, a landlord or a seller know who they are dealing with.
                Your initials are used if you would rather not.
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-5" data-id="element-7330" data-mp="5muewp4mnjux">
                {photo ? <img src={photo} alt="Your profile photo" className="h-24 w-24 rounded-full border border-line object-cover" data-id="element-7331" data-mp="lew73px9pfp" /> : <p aria-hidden className="flex h-24 w-24 items-center justify-center rounded-full border border-line bg-canvas text-h3 font-semibold text-ink-muted" data-id="element-7332" data-mp="20og2y5i6xvqi">
                    {identity.photoInitials}
                  </p>}
                <div data-id="element-7333" data-mp="1cz4yoe1mtj7m">
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={event => pickPhoto(event.target.files?.[0])} data-id="element-7334" data-mp="1cyloxkm4y14o" />
                  <div className="flex flex-wrap gap-2" data-id="element-7335" data-mp="17z8twcl41nr0">
                    <Button variant="secondary" icon={<UploadIcon className="h-4 w-4" data-id="element-7337" data-mp="2xf87c5vrhzcg" />} onClick={() => fileRef.current?.click()} data-id="element-7336" data-mp="181v2mfjqm9at">
                      {photo ? 'Choose another' : 'Choose a photo'}
                    </Button>
                    {photo && <Button variant="ghost" onClick={() => setPhoto(null)} data-id="element-7338" data-mp="ls4s742idmpe">
                        Remove
                      </Button>}
                  </div>
                  <p className="mt-2 max-w-[46ch] text-caption text-ink-subtle" data-id="element-7339" data-mp="vp129e822gw2">
                    JPG, PNG or WebP, up to 5 MB. Visible according to your profile privacy setting.
                  </p>
                  {photoError && <p role="alert" className="mt-2 text-body-sm text-danger-700" data-id="element-7340" data-mp="15esx89k2hc12">
                      {photoError}
                    </p>}
                </div>
              </div>
            </>}

          {step.key === 'interests' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7341" data-mp="3l79rkfc5ghpc">What are you here for?</h2>
              <p className="mt-2 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7342" data-mp="3g8fc9nde5b9z">
                Used to order what you see first. You can change these, or switch personalisation off
                entirely, in Settings.
              </p>
              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-7343" data-mp="1p3lv2jkhn7ip">
                {INTERESTS.map(item => {
              const active = interests.includes(item);
              return <li key={item} data-id="element-7344" data-mp="1y5laj7fuu3jj">
                      <button type="button" aria-pressed={active} onClick={() => setInterests(current => active ? current.filter(x => x !== item) : [...current, item])} className={`flex min-h-touch items-center gap-2 rounded-full border px-4 text-body-sm font-medium transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-50 text-brand-900' : 'border-line text-ink-muted hover:border-line-strong hover:text-ink'}`} data-id="element-7345" data-mp="2334pyaoagmd2">
                        {active ? <CheckCircle2Icon aria-hidden className="h-4 w-4" data-id="element-7346" data-mp="2qxwxx2ycs280" /> : <CircleIcon aria-hidden className="h-4 w-4" data-id="element-7347" data-mp="2mxmv1z5uxrxd" />}
                        {item}
                      </button>
                    </li>;
            })}
              </ul>
            </>}

          {step.key === 'notifications' && <>
              <h2 className="text-h4 font-semibold text-ink" data-id="element-7348" data-mp="39vzrlv1uinc5">Notifications</h2>
              <div className="mt-3" data-id="element-7349" data-mp="35wik6w2uzg4c">
                <Toggle checked={notifications} onChange={setNotifications} label="Send me updates that need my attention" description="Application decisions, order and escrow changes, rent reminders, campus events. Safety and security messages are always sent." data-id="element-7350" data-mp="35vh2i8rc7vdn" />
              </div>
              <p className="mt-4 max-w-[64ch] text-body-sm text-ink-muted" data-id="element-7351" data-mp="px1hd8h6wvav">
                You will land in{' '}
                <span className="font-medium text-ink" data-id="element-7352" data-mp="b8wx402hobpz">
                  {destination === '/' ? 'your student home' : `the console at ${destination}`}
                </span>
                , because that is the role this account holds. Add a role later and the switcher will
                show it.
              </p>
            </>}
        </section>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-7353" data-mp="tymp6fevaje7">
          <Button variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-7355" data-mp="yvmw0sjjjq6d" />} onClick={() => setIndex(current => Math.max(0, current - 1))} disabled={index === 0} data-id="element-7354" data-mp="u07hdydplq8w">
            Back
          </Button>
          <Button variant="primary" icon={last ? undefined : <ArrowRightIcon className="h-4 w-4" data-id="element-7357" data-mp="21oqzvp95marf" />} onClick={commit} data-id="element-7356" data-mp="3joqcucozxj5z">
            {last ? 'Enter Campuzon' : 'Save and continue'}
          </Button>
          {!last && <Button variant="ghost" onClick={() => setIndex(current => current + 1)} data-id="element-7358" data-mp="2aqi5ku3p2hla">
              Skip this step
            </Button>}
        </div>
      </main>
    </div>;
}